import { useEffect, useMemo, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  HiOutlineMagnifyingGlass,
  HiOutlinePencilSquare,
  HiOutlineEye,
  HiOutlineUser,
  HiOutlineCalendarDays,
  HiOutlineFolder,
  HiOutlinePlus,
} from "react-icons/hi2";

import PageHeader from "../../../components/ui/pageHeader/PageHeader";
import PageCard from "../../../components/ui/pageCard/PageCard";
import PageLoader from "../../../components/ui/pageLoader/PageLoader";
import PageEmpty from "../../../components/ui/pageEmpty/PageEmpty";
import Modal from "../../../components/ui/modal/Modal";

import DossierEditModal from "../../../components/dossiers/DossierEditModal";
import DossierForm from "../../../components/forms/dossier/DossierForm";

import {
  getDossiers,
  setDossierPage,
} from "../../../redux/features/dossiers/dossierSlice";

import {
  getUsers,
  getOrganizationCoaches,
} from "../../../redux/features/users/userSlice";

import "./DossiersPage.css";

const COACH_ROLES = ["POWER_INTERNAL_COACH", "POWER_EXTERNAL_COACH"];

const STEP_LABELS = {
  aanmelding: "Aanmelding",
  administratie: "Administratie",
  begeleiding: "Begeleiding",
  vdab: "VDAB",
  kld: "KLD",
  afronding: "Afronding",
};

const STATUS_LABELS = {
  active: "Actief",
  paused: "Gepauzeerd",
  completed: "Afgerond",
  cancelled: "Geannuleerd",
};

export default function DossiersPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { user } = useSelector((state) => state.auth);

  const {
    dossiers = [],
    pagination = {},
    isLoading,
    error,
  } = useSelector((state) => state.dossiers || {});

  const { users = [], organizationCoaches = [] } = useSelector(
    (state) => state.users || {},
  );

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [status, setStatus] = useState("");
  const [currentStep, setCurrentStep] = useState("");
  const [selectedCoach, setSelectedCoach] = useState("");

  const [selectedDossier, setSelectedDossier] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const isCoach =
    user?.role === "POWER_INTERNAL_COACH" ||
    user?.role === "POWER_EXTERNAL_COACH";

  const isOrganizationAdmin = user?.role === "POWER_ORGANIZATION_ADMIN";
  const isPowerAdmin = user?.role === "POWER_ADMIN";

  const canCreateTraject = isPowerAdmin;

  /**
   * =====================================================
   * DEBOUNCE SEARCH
   * =====================================================
   */

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 450);

    return () => clearTimeout(timer);
  }, [search]);

  /**
   * =====================================================
   * RESET PAGE WHEN FILTERS CHANGE
   * =====================================================
   */

  useEffect(() => {
    if ((pagination.page || 1) !== 1) {
      dispatch(setDossierPage(1));
    }
  }, [dispatch, debouncedSearch, status, currentStep, selectedCoach]);

  /**
   * =====================================================
   * LOAD DOSSIERS FROM SERVER
   * =====================================================
   */

  useEffect(() => {
    dispatch(
      getDossiers({
        page: pagination.page || 1,
        limit: pagination.limit || 20,
        search: debouncedSearch || undefined,
        status: status || undefined,
        current_step: currentStep || undefined,
        coach: selectedCoach || undefined,
      }),
    );
  }, [
    dispatch,
    pagination.page,
    pagination.limit,
    debouncedSearch,
    status,
    currentStep,
    selectedCoach,
  ]);

  /**
   * =====================================================
   * LOAD COACH OPTIONS
   * =====================================================
   */

  useEffect(() => {
    if (!user) return;

    if (isOrganizationAdmin && user.organization) {
      dispatch(getOrganizationCoaches(user.organization));
      return;
    }

    if (isPowerAdmin) {
      dispatch(
        getUsers({
          limit: 500,
        }),
      );
    }
  }, [dispatch, user, isOrganizationAdmin, isPowerAdmin]);

  /**
   * =====================================================
   * COACH OPTIONS
   * =====================================================
   */

  const coachOptions = useMemo(() => {
    if (isOrganizationAdmin) {
      return (organizationCoaches || []).filter((coach) =>
        COACH_ROLES.includes(coach.role),
      );
    }

    if (isPowerAdmin) {
      return (users || []).filter((item) => COACH_ROLES.includes(item.role));
    }

    return [];
  }, [users, organizationCoaches, isOrganizationAdmin, isPowerAdmin]);

  const handlePreviousPage = () => {
    if ((pagination.page || 1) > 1) {
      dispatch(setDossierPage((pagination.page || 1) - 1));
    }
  };

  const handleNextPage = () => {
    if ((pagination.page || 1) < (pagination.pages || 1)) {
      dispatch(setDossierPage((pagination.page || 1) + 1));
    }
  };

  const openEditModal = (dossier) => {
    setSelectedDossier(dossier);
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setSelectedDossier(null);
    setIsEditModalOpen(false);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const refreshDossiers = () => {
    dispatch(
      getDossiers({
        page: pagination.page || 1,
        limit: pagination.limit || 20,
        search: debouncedSearch || undefined,
        status: status || undefined,
        current_step: currentStep || undefined,
        coach: selectedCoach || undefined,
      }),
    );
  };

  if (isLoading && dossiers.length === 0) {
    return <PageLoader title="Trajecten laden..." />;
  }

  return (
    <div className="dossiers-page">
      <PageHeader
        eyebrow="POWER"
        title="Trajecten"
        description="Overzicht van POWER trajecten."
        actions={
          canCreateTraject && (
            <button
              type="button"
              className="dossiers-page__create-button"
              onClick={() => setIsCreateModalOpen(true)}
            >
              <HiOutlinePlus />
              <span>Nieuw traject</span>
            </button>
          )
        }
      />

      {error && <div className="dossiers-page__error">{error}</div>}

      <PageCard className="dossiers-page__filters">
        <div className="dossiers-page__filters-grid">
          <div className="dossiers-page__search">
            <HiOutlineMagnifyingGlass />

            <input
              type="text"
              value={search}
              placeholder="Zoeken op cliënt..."
              autoComplete="off"
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Alle statussen</option>
            <option value="active">Actief</option>
            <option value="paused">Gepauzeerd</option>
            <option value="completed">Afgerond</option>
            <option value="cancelled">Geannuleerd</option>
          </select>

          <select
            value={currentStep}
            onChange={(e) => setCurrentStep(e.target.value)}
          >
            <option value="">Alle stappen</option>
            <option value="aanmelding">Aanmelding</option>
            <option value="administratie">Administratie</option>
            <option value="begeleiding">Begeleiding</option>
            <option value="vdab">VDAB</option>
            <option value="kld">KLD</option>
            <option value="afronding">Afronding</option>
          </select>

          {!isCoach && (
            <select
              value={selectedCoach}
              onChange={(e) => setSelectedCoach(e.target.value)}
            >
              <option value="">Alle coaches</option>

              {coachOptions.map((coach) => (
                <option key={coach._id} value={coach._id}>
                  {coach.first_name} {coach.last_name}
                </option>
              ))}
            </select>
          )}
        </div>
      </PageCard>

      {isLoading && dossiers.length > 0 && (
        <div className="dossiers-page__small-loading">
          Trajecten bijwerken...
        </div>
      )}

      {!isLoading && dossiers.length === 0 && (
        <PageEmpty
          icon={<HiOutlineFolder />}
          title="Geen trajecten gevonden"
          description="Er zijn geen trajecten die overeenkomen met deze filters."
        />
      )}

      {dossiers.length > 0 && (
        <div className="dossiers-page__grid">
          {dossiers.map((dossier) => (
            <PageCard key={dossier._id} className="dossiers-page__card">
              <div className="dossiers-page__card-top">
                <div>
                  <h3>{dossier?.client?.display_name || "Onbekende cliënt"}</h3>

                  <span>Traject #{dossier.sequence_number || "-"}</span>
                </div>

                <div
                  className={`dossiers-page__status dossiers-page__status--${dossier.status}`}
                >
                  {STATUS_LABELS[dossier.status] || dossier.status || "-"}
                </div>
              </div>

              <div className="dossiers-page__meta">
                <div>
                  <strong>
                    <HiOutlineUser />
                    Coach
                  </strong>

                  <span>
                    {dossier?.coach
                      ? `${dossier.coach.first_name} ${dossier.coach.last_name}`
                      : "-"}
                  </span>
                </div>

                <div>
                  <strong>
                    <HiOutlineCalendarDays />
                    Schooljaar
                  </strong>

                  <span>{dossier?.school_year?.name || "-"}</span>
                </div>

                <div>
                  <strong>Stap</strong>

                  <span>
                    {STEP_LABELS[dossier.current_step] ||
                      dossier.current_step ||
                      "-"}
                  </span>
                </div>
              </div>

              <div className="dossiers-page__actions">
                <button
                  type="button"
                  onClick={() => navigate(`/dashboard/dossiers/${dossier._id}`)}
                >
                  <HiOutlineEye />
                  <span>Open</span>
                </button>

                {!isCoach && (
                  <button type="button" onClick={() => openEditModal(dossier)}>
                    <HiOutlinePencilSquare />
                    <span>Bewerken</span>
                  </button>
                )}
              </div>
            </PageCard>
          ))}
        </div>
      )}

      {pagination?.pages > 1 && (
        <div className="dossiers-page__pagination">
          <button
            type="button"
            disabled={(pagination.page || 1) <= 1}
            onClick={handlePreviousPage}
          >
            Vorige
          </button>

          <span>
            Pagina {pagination.page || 1} van {pagination.pages || 1} ·{" "}
            {pagination.total || 0} trajecten
          </span>

          <button
            type="button"
            disabled={(pagination.page || 1) >= (pagination.pages || 1)}
            onClick={handleNextPage}
          >
            Volgende
          </button>
        </div>
      )}

      {canCreateTraject && (
        <Modal
          isOpen={isCreateModalOpen}
          onClose={closeCreateModal}
          title="Nieuw traject"
          description="Maak een nieuw traject aan en wijs het toe aan een coach."
        >
          <DossierForm
            onSuccess={() => {
              closeCreateModal();
              refreshDossiers();
            }}
            onCancel={closeCreateModal}
          />
        </Modal>
      )}

      <DossierEditModal
        isOpen={isEditModalOpen}
        dossier={selectedDossier}
        coaches={coachOptions}
        onClose={closeEditModal}
      />
    </div>
  );
}
